import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, MenuItem, TextField, Typography, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, Chip, Divider, Alert, alpha, Switch, FormControlLabel
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import {
  Add, Delete, AttachFile, CloudUpload, Cancel,
  ShoppingCart, Search, Refresh, PictureAsPdf, Visibility, PersonAdd
} from '@mui/icons-material';
import GridOnIcon from '@mui/icons-material/GridOn';
import InputAdornment from '@mui/material/InputAdornment';
import axios from 'axios';
import ClienteRapidoDialog from '../components/ClienteRapidoDialog';
import ProductoRapidoDialog from '../components/ProductoRapidoDialog';
import ProductoBuscadorDialog from '../components/ProductoBuscadorDialog';
import { obtenerEmpresaConfig } from '../utils/empresaConfig';
import { useResizableCols } from '../hooks/useResizableCols';

const TIPOS_COMPROBANTE = ['FACTURA', 'BOLETA', 'TICKET', 'NOTA DE DÉBITO', 'OTRO'];
const CONDICIONES_PAGO  = ['CONTADO', 'CRÉDITO 15 DÍAS', 'CRÉDITO 30 DÍAS', 'CRÉDITO 60 DÍAS'];

const COL_DEFAULTS_COMPRA = {
  producto:  220,
  descripcion: 220,
  cant:      75,
  umedida:   65,
  costo:     110,
  igv:       65,
  almacen:   150,
  subtotal:  95,
  total:     95,
  acciones:  45,
};

const ResizeHandleCompra = ({ onMouseDown }) => {
  const [hover, setHover] = useState(false);
  return (
    <span
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'absolute', right: 0, top: 0, bottom: 0, width: 8,
        cursor: 'col-resize', userSelect: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <span style={{
        width: hover ? 3 : 2, height: hover ? '70%' : '55%', borderRadius: 1,
        background: hover ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.4)',
        pointerEvents: 'none', transition: 'all 0.12s',
      }} />
    </span>
  );
};

const initialDetalle = {
  producto_id: '', producto_obj: null,
  descripcion: '', cantidad: 1, unidad_medida: 'NIU',
  costo_unitario: 0, igv_porcentaje: 18,
  igv_monto: 0, subtotal: 0, total: 0,
  almacen_id: '',
};

const initialForm = {
  serie: 'FC', numero: '',
  fecha_emision: new Date().toLocaleDateString('en-CA'),
  fecha_vencimiento: '',
  proveedor_id: '', proveedor_obj: null,
  moneda: 'PEN', tipo_cambio: '',
  subtotal: 0, total_igv: 0, total: 0,
  tipo_comprobante_proveedor: 'FACTURA',
  condicion_pago: 'CONTADO',
  observaciones: '',
  almacen_id: '', almacen_obj: null,
  sucursal_id: '',
  detalles: [{ ...initialDetalle }],
};

function calcDetalle(det, conIgv = false) {
  const cantidad = parseFloat(det.cantidad) || 0;
  const costo    = parseFloat(det.costo_unitario) || 0;
  const igvPct   = parseFloat(det.igv_porcentaje) || 0;
  let subtotal, igv_monto, total;
  if (conIgv && igvPct > 0) {
    total     = parseFloat((cantidad * costo).toFixed(2));
    subtotal  = parseFloat((total / (1 + igvPct / 100)).toFixed(2));
    igv_monto = parseFloat((total - subtotal).toFixed(2));
  } else {
    subtotal  = parseFloat((cantidad * costo).toFixed(2));
    igv_monto = parseFloat((subtotal * igvPct / 100).toFixed(2));
    total     = parseFloat((subtotal + igv_monto).toFixed(2));
  }
  return { ...det, subtotal, igv_monto, total };
}

function calcTotales(detalles) {
  const subtotal  = detalles.reduce((s, d) => s + (parseFloat(d.subtotal)  || 0), 0);
  const total_igv = detalles.reduce((s, d) => s + (parseFloat(d.igv_monto) || 0), 0);
  return {
    subtotal:  parseFloat(subtotal.toFixed(2)),
    total_igv: parseFloat(total_igv.toFixed(2)),
    total:     parseFloat((subtotal + total_igv).toFixed(2))
  };
}

const SectionHeader = ({ children }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', gap: 0.5,
    borderLeft: '3px solid', borderColor: 'primary.main',
    pl: 1.5, py: 0.25, mb: 1.5, bgcolor: t => alpha(t.palette.primary.main, 0.06), borderRadius: '0 4px 4px 0'
  }}>
    <Typography variant="subtitle2" color="primary.dark" fontWeight={700}>{children}</Typography>
  </Box>
);

const CompraPage = () => {
  const { widths: colW, startResize } = useResizableCols('compra-detail-cols', COL_DEFAULTS_COMPRA);
  const [compras, setCompras]           = useState([]);
  const [totalCompras, setTotalCompras] = useState(0);
  const [page, setPage]                 = useState(1);
  const [search, setSearch]             = useState('');
  const [desde, setDesde]               = useState('');
  const [hasta, setHasta]               = useState('');
  const [loading, setLoading]           = useState(false);

  const [open, setOpen]         = useState(false);
  const [form, setForm]         = useState({ ...initialForm });
  const [guardando, setGuardando] = useState(false);
  const [error, setError]       = useState('');

  const [proveedores, setProveedores]   = useState([]);
  const [provInputValue, setProvInputValue] = useState('');
  const [openNuevoProv, setOpenNuevoProv] = useState(false);
  const [openNuevoProducto, setOpenNuevoProducto] = useState(false);
  const [productoInputActivo, setProductoInputActivo] = useState({ idx: 0, texto: '' });
  const [openBuscadorProducto, setOpenBuscadorProducto] = useState(false);
  const [buscadorRowIdx, setBuscadorRowIdx] = useState(0);
  const [almacenes, setAlmacenes]       = useState([]);
  const [sucursales, setSucursales]     = useState([]);
  const [productOptions, setProductOptions]       = useState({});
  const [productInputValues, setProductInputValues] = useState({});
  const [preciosConIgv, setPreciosConIgv] = useState(false);

  const [adjuntoFile, setAdjuntoFile]       = useState(null);
  const [subiendoAdjunto, setSubiendoAdjunto] = useState(false);

  const [viewCompra, setViewCompra] = useState(null);

  // Importar desde Orden de Compra
  const [openOCDialog, setOpenOCDialog]   = useState(false);
  const [ordenesCompra, setOrdenesCompra] = useState([]);
  const [ocLoading, setOcLoading]         = useState(false);

  const [importDialog, setImportDialog]         = useState(false);
  const [importFile, setImportFile]             = useState(null);
  const [importLoading, setImportLoading]       = useState(false);
  const [importResult, setImportResult]         = useState(null);

  // Adjunto management dialog
  const [openAdjuntoDialog, setOpenAdjuntoDialog] = useState(false);
  const [adjuntoDialogCompra, setAdjuntoDialogCompra] = useState(null);
  const [adjuntoDialogFile, setAdjuntoDialogFile] = useState(null);
  const [adjuntoDialogLoading, setAdjuntoDialogLoading] = useState(false);

  // PDF dialog state
  const [openCompraPdfDialog, setOpenCompraPdfDialog] = useState(false);
  const [compraPdfTarget, setCompraPdfTarget] = useState(null);
  const [compraPdfFormatoSeleccionado, setCompraPdfFormatoSeleccionado] = useState(() => localStorage.getItem('compra_pdf_formato_default') || 'A4');
  const [guardarCompraPdfComoPredeterminado, setGuardarCompraPdfComoPredeterminado] = useState(true);
  const [compraPdfResolucion, setCompraPdfResolucion] = useState({});
  const [compraPdfResolucionCargando, setCompraPdfResolucionCargando] = useState(false);

  const actualizarResolucionFormatoCompra = async (nuevoFormato) => {
    if (!compraPdfTarget) return;
    setCompraPdfResolucionCargando(true);
    try {
      const res = await axios.get(`/api/reporte-pdf/resuelve-compra/${compraPdfTarget.id}`, { params: { formato: nuevoFormato } });
      setCompraPdfResolucion(res.data || {});
    } catch (err) {
      console.error('Error resolving COMPRA PDF template:', err);
    } finally {
      setCompraPdfResolucionCargando(false);
    }
  };

  const abrirDialogoPdfCompra = async (compra) => {
    const target = typeof compra === 'object' ? compra : { id: compra };
    const formatoGuardado = localStorage.getItem('compra_pdf_formato_default') || 'A4';
    setCompraPdfTarget(target);
    setCompraPdfFormatoSeleccionado(formatoGuardado);
    setGuardarCompraPdfComoPredeterminado(true);
    setCompraPdfResolucion({});
    setOpenCompraPdfDialog(true);
    setCompraPdfResolucionCargando(true);
    try {
      const res = await axios.get(`/api/reporte-pdf/resuelve-compra/${target.id}`, { params: { formato: formatoGuardado } });
      setCompraPdfResolucion(res.data || {});
    } catch (err) {
      console.error('Error resolving COMPRA PDF template:', err);
    } finally {
      setCompraPdfResolucionCargando(false);
    }
  };

  const cerrarDialogoPdfCompra = () => {
    setOpenCompraPdfDialog(false);
    setCompraPdfTarget(null);
  };

  const descargarPdfCompra = () => {
    if (!compraPdfTarget) return;
    const formato = String(compraPdfFormatoSeleccionado || 'A4').toUpperCase();
    if (guardarCompraPdfComoPredeterminado) {
      localStorage.setItem('compra_pdf_formato_default', formato);
    }
    const tk = localStorage.getItem('token') || '';
    const query = new URLSearchParams({ formato, mode: 'inline', _ts: Date.now() }).toString();
    window.open(`/api/reporte-pdf/compra/${compraPdfTarget.id}?${query}&token=${tk}`, '_blank');
    cerrarDialogoPdfCompra();
  };

  // ─ Lista ──────────────────────────────────────────────────────────────────

  const fetchCompras = useCallback(async () => {
    setLoading(true);
    try {
      const params = { search, page, limit: 50 };
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      const res = await axios.get('/api/compras', { params });
      setCompras(res.data.data || []);
      setTotalCompras(res.data.total || 0);
    } catch { setCompras([]); }
    setLoading(false);
  }, [search, page, desde, hasta]);

  useEffect(() => { fetchCompras(); }, [fetchCompras]);

  const handleExportExcel = async () => {
    try {
      const { exportCompraWorkbook } = await import('../utils/excel/compraWorkbook');
      await exportCompraWorkbook({
        compras,
        filtros: { search, desde, hasta },
        usuario: localStorage.getItem('usuario') || ''
      });
    } catch (err) {
      alert('Error al exportar Excel: ' + err.message);
    }
  };

  // ─ Catálogos ──────────────────────────────────────────────────────────────

  const fetchProveedoresInit = useCallback(async () => {
    try {
      const res = await axios.get('/api/auxiliares', { params: { tipo: 'PROVEEDOR' } });
      setProveedores(res.data || []);
    } catch { setProveedores([]); }
  }, []);

  const fetchAlmacenes  = async () => { try { const r = await axios.get('/api/almacen/mis-almacenes');     setAlmacenes(r.data  || []); } catch { setAlmacenes([]); } };
  const fetchSucursales = async () => { try { const r = await axios.get('/api/sucursales/mis-sucursales');  setSucursales(r.data || []); } catch { setSucursales([]); } };

  const debouncedFetchRefs = useRef({});

  const fetchProductosForRow = useCallback((rowIdx, q) => {
    if (!debouncedFetchRefs.current[rowIdx]) {
      debouncedFetchRefs.current[rowIdx] = debounce(async (s) => {
        try {
          const res = await axios.get('/api/productos', { params: { search: s, limit: 30 } });
          setProductOptions(prev => ({ ...prev, [rowIdx]: res.data?.data || res.data || [] }));
        } catch {
          setProductOptions(prev => ({ ...prev, [rowIdx]: [] }));
        }
      }, 300);
    }
    debouncedFetchRefs.current[rowIdx](q);
  }, []);

  // Load initial product list for new rows
  const loadInitialProducts = useCallback(async (rowIdx) => {
    try {
      const res = await axios.get('/api/productos', { params: { search: '', limit: 40 } });
      setProductOptions(prev => ({ ...prev, [rowIdx]: res.data?.data || res.data || [] }));
    } catch {}
  }, []);

  // ─ Abrir formulario ───────────────────────────────────────────────────────

  const handleNuevo = () => {
    setForm({ ...initialForm, detalles: [{ ...initialDetalle }] });
    setProvInputValue('');
    setError('');
    obtenerEmpresaConfig().then(cfg => setPreciosConIgv(Boolean(cfg?.precios_incluyen_igv))).catch(() => {});
    setAdjuntoFile(null);
    debouncedFetchRefs.current = {};
    setProductOptions({});
    setProductInputValues({});
    fetchAlmacenes();
    fetchSucursales();
    fetchProveedoresInit();
    loadInitialProducts(0);
    setOpen(true);
  };

  // ─ Formulario ─────────────────────────────────────────────────────────────

  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleDetalleChange = (idx, field, value) => {
    setForm(prev => {
      const detalles = prev.detalles.map((d, i) => i !== idx ? d : calcDetalle({ ...d, [field]: value }, preciosConIgv));
      return { ...prev, detalles, ...calcTotales(detalles) };
    });
  };

  const handleProductoSelect = (idx, prod) => {
    if (!prod) return;
    // Advertencia si el producto no afecta kardex
    if (prod.afecta_kardex === false || prod.afecta_kardex === 0) {
      alert(`El producto "${prod.nombre}" no está habilitado para Kardex. Puede agregarlo igualmente, pero no afectará el stock.`);
    }
    setForm(prev => {
      const detalles = prev.detalles.map((d, i) => {
        if (i !== idx) return d;
        return calcDetalle({
          ...d,
          producto_id: prod.id,
          producto_obj: prod,
          descripcion: prod.nombre || '',
          unidad_medida: prod.unidad_medida || 'NIU',
          costo_unitario: prod.precio_compra || prod.precio_costo || 0,
        }, preciosConIgv);
      });
      return { ...prev, detalles, ...calcTotales(detalles) };
    });
  };

  const handleBuscadorSelectCompra = (prod) => {
    handleProductoSelect(buscadorRowIdx, prod);
    setProductInputValues(prev => ({ ...prev, [buscadorRowIdx]: prod.nombre || '' }));
  };

  const cargarOrdenesCompra = async () => {
    setOcLoading(true);
    try {
      const tk = localStorage.getItem('token');
      const r = await axios.get('/api/orden-compra/disponibles', { headers: { Authorization: `Bearer ${tk}` } });
      setOrdenesCompra(r.data || []);
    } catch { setOrdenesCompra([]); }
    setOcLoading(false);
  };

  const importarOrdenCompra = (oc) => {
    const newDetalles = oc.detalles.map(d => calcDetalle({
      ...initialDetalle,
      producto_id: d.producto_id || '',
      producto_obj: d.producto_id ? { id: d.producto_id, nombre: d.producto_nombre, codigo: d.producto_codigo } : null,
      descripcion: d.descripcion_producto || d.producto_nombre || '',
      unidad_medida: d.unidad_medida || 'NIU',
      cantidad: d.cantidad || 1,
      costo_unitario: d.precio_unitario || 0,
    }, preciosConIgv));
    const newVals = {};
    oc.detalles.forEach((d, i) => { newVals[i] = d.producto_nombre || d.descripcion_producto || ''; });
    setProductInputValues(newVals);
    if (oc.proveedor_id) {
      setProvInputValue(oc.proveedor_ruc ? `${oc.proveedor_ruc} - ${oc.proveedor_nombre}` : oc.proveedor_nombre || '');
      setForm(prev => ({
        ...prev,
        proveedor_id: oc.proveedor_id,
        proveedor_obj: { id: oc.proveedor_id, razon_social: oc.proveedor_nombre, numero_documento: oc.proveedor_ruc },
        detalles: newDetalles,
        ...calcTotales(newDetalles),
      }));
    } else {
      setForm(prev => ({ ...prev, detalles: newDetalles, ...calcTotales(newDetalles) }));
    }
    setOpenOCDialog(false);
  };

  const handleAddDetalle = () => {
    setForm(prev => {
      const newIdx = prev.detalles.length;
      loadInitialProducts(newIdx);
      return { ...prev, detalles: [...prev.detalles, { ...initialDetalle }] };
    });
  };

  const handleRemoveDetalle = (idx) => {
    setForm(prev => {
      const detalles = prev.detalles.filter((_, i) => i !== idx);
      return { ...prev, detalles, ...calcTotales(detalles.length ? detalles : [{ ...initialDetalle }]) };
    });
  };

  // ─ Guardar ────────────────────────────────────────────────────────────────

  const handleGuardar = async () => {
    setError('');
    if (!form.proveedor_id) { setError('Seleccione un proveedor'); return; }
    if (!form.almacen_id)   { setError('Seleccione un almacén'); return; }
    if (!form.numero)       { setError('Ingrese el número del comprobante'); return; }
    const detalles = form.detalles.filter(d => d.producto_id);
    if (!detalles.length)   { setError('Agregue al menos un producto'); return; }

    setGuardando(true);
    try {
      const res = await axios.post('/api/compras', {
        serie:    form.serie,
        numero:   form.numero,
        fecha_emision:    form.fecha_emision,
        fecha_vencimiento: form.fecha_vencimiento || null,
        proveedor_id:     form.proveedor_id,
        moneda:           form.moneda,
        tipo_cambio:      form.tipo_cambio || null,
        subtotal:         form.subtotal,
        total_igv:        form.total_igv,
        total:            form.total,
        tipo_comprobante_proveedor: form.tipo_comprobante_proveedor,
        condicion_pago:   form.condicion_pago,
        observaciones:    form.observaciones || null,
        almacen_id:       form.almacen_id,
        sucursal_id:      form.sucursal_id || null,
        detalles: detalles.map(d => ({
          producto_id:    d.producto_id,
          descripcion:    d.descripcion || null,
          cantidad:       parseFloat(d.cantidad)      || 0,
          unidad_medida:  d.unidad_medida              || 'NIU',
          costo_unitario: parseFloat(d.costo_unitario) || 0,
          igv_porcentaje: parseFloat(d.igv_porcentaje) || 18,
          igv_monto:      parseFloat(d.igv_monto)      || 0,
          subtotal:       parseFloat(d.subtotal)        || 0,
          total:          parseFloat(d.total)           || 0,
          almacen_id:     d.almacen_id || null,
        }))
      });

      if (adjuntoFile) {
        const fd = new FormData();
        fd.append('archivo', adjuntoFile);
        await axios.post(`/api/compras/${res.data.id}/adjunto`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        }).catch(e => console.error('Adjunto:', e.message));
      }

      setOpen(false);
      fetchCompras();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setGuardando(false);
  };

  const handleAnular = async (id) => {
    if (!window.confirm('¿Anular esta compra? Se revertirán los movimientos de kardex.')) return;
    try {
      await axios.put(`/api/compras/${id}/anular`);
      fetchCompras();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al anular');
    }
  };

  const handleVerDetalle = async (c) => {
    try {
      const res = await axios.get(`/api/compras/${c.id}`);
      setViewCompra(res.data);
    } catch { setViewCompra(c); }
  };

  const handlePdf = (id) => abrirDialogoPdfCompra({ id });

  const monedaSimbolo = (m) => m === 'USD' ? '$' : 'S/';

  const downloadPlantillaCompras = async () => {
    const ExcelJS = (await import('exceljs')).default;
    const { saveAs } = await import('file-saver');
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Compras');
    ws.columns = [
      { header: 'serie',                  key: 'serie',                  width: 10 },
      { header: 'numero',                 key: 'numero',                 width: 12 },
      { header: 'fecha',                  key: 'fecha',                  width: 14 },
      { header: 'proveedor_ruc',          key: 'proveedor_ruc',          width: 16 },
      { header: 'proveedor_razon_social', key: 'proveedor_razon_social', width: 30 },
      { header: 'tipo_comprobante',       key: 'tipo_comprobante',       width: 16 },
      { header: 'condicion_pago',         key: 'condicion_pago',         width: 16 },
      { header: 'moneda',                 key: 'moneda',                 width: 8  },
      { header: 'tipo_cambio',            key: 'tipo_cambio',            width: 12 },
      { header: 'sucursal',               key: 'sucursal',               width: 20 },
      { header: 'almacen',                key: 'almacen',                width: 20 },
      { header: 'observaciones',          key: 'observaciones',          width: 25 },
      { header: 'producto_codigo',        key: 'producto_codigo',        width: 16 },
      { header: 'descripcion_item',       key: 'descripcion_item',       width: 28 },
      { header: 'cantidad',               key: 'cantidad',               width: 10 },
      { header: 'unidad_medida',          key: 'unidad_medida',          width: 14 },
      { header: 'costo_unitario',         key: 'costo_unitario',         width: 14 },
      { header: 'igv_porcentaje',         key: 'igv_porcentaje',         width: 14 },
    ];
    const hdr = ws.getRow(1);
    hdr.height = 22;
    hdr.eachCell(cell => {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0D47A1' } };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
    });
    const today = new Date().toLocaleDateString('en-CA');
    ws.addRow({ serie: 'FC', numero: '000001', fecha: today, proveedor_ruc: '20100070970',
      proveedor_razon_social: 'PROVEEDOR EJEMPLO S.A.C.', tipo_comprobante: 'FACTURA',
      condicion_pago: 'CONTADO', moneda: 'PEN', tipo_cambio: '', sucursal: 'Principal',
      almacen: 'Almacén Principal', observaciones: '', producto_codigo: 'PROD-001',
      descripcion_item: 'Producto uno', cantidad: 10, unidad_medida: 'NIU', costo_unitario: 25.00, igv_porcentaje: 18 });
    ws.addRow({ serie: 'FC', numero: '000001', fecha: today, proveedor_ruc: '20100070970',
      proveedor_razon_social: 'PROVEEDOR EJEMPLO S.A.C.', tipo_comprobante: 'FACTURA',
      condicion_pago: 'CONTADO', moneda: 'PEN', tipo_cambio: '', sucursal: 'Principal',
      almacen: 'Almacén Principal', observaciones: '', producto_codigo: 'PROD-002',
      descripcion_item: 'Producto dos', cantidad: 5, unidad_medida: 'NIU', costo_unitario: 50.00, igv_porcentaje: 18 });
    const ws2 = wb.addWorksheet('Instrucciones');
    ws2.getCell('A1').value = 'INSTRUCCIONES — IMPORTAR COMPRAS DESDE EXCEL';
    ws2.getCell('A1').font = { bold: true, size: 13 };
    [
      ['Campo', 'Descripción'],
      ['serie', 'Serie del comprobante (ej: FC, BV). Default: FC'],
      ['numero', 'Número del comprobante. Filas con mismo serie+numero+proveedor_ruc = 1 compra'],
      ['fecha', 'Fecha de emisión (YYYY-MM-DD). Default: hoy'],
      ['proveedor_ruc', 'RUC/DNI del proveedor. Si no existe y hay razon_social, se crea automáticamente'],
      ['proveedor_razon_social', 'Razón social del proveedor (solo si necesita crearse)'],
      ['tipo_comprobante', 'FACTURA / BOLETA / TICKET / OTRO. Default: FACTURA'],
      ['condicion_pago', 'CONTADO / CRÉDITO 30 DÍAS / etc. Default: CONTADO'],
      ['moneda', 'PEN (soles) o USD (dólares). Default: PEN'],
      ['tipo_cambio', 'Tipo de cambio si moneda=USD. Dejar vacío si es PEN'],
      ['sucursal', 'Nombre de sucursal. Si vacío, usa la primera disponible'],
      ['almacen', 'Nombre de almacén. Si vacío, usa el primero disponible'],
      ['observaciones', 'Observaciones de la compra (opcional)'],
      ['producto_codigo', 'Código del producto en InsightSoft (debe existir)'],
      ['descripcion_item', 'Descripción del ítem (opcional)'],
      ['cantidad', 'Cantidad comprada'],
      ['unidad_medida', 'Unidad: NIU, KGM, UND, etc. Default: NIU'],
      ['costo_unitario', 'Precio de compra por unidad'],
      ['igv_porcentaje', 'Porcentaje de IGV: 18 (gravado) o 0 (exonerado). Default: 18'],
    ].forEach((row, i) => {
      const r = ws2.addRow(row);
      if (i === 0) { r.font = { bold: true }; r.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } }; }
    });
    ws2.columns = [{ width: 25 }, { width: 70 }];
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'Plantilla_Importar_Compras.xlsx');
  };

  const handleImportCompras = async () => {
    if (!importFile) return;
    setImportLoading(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const res = await axios.post('/api/compras/importar-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(res.data);
      if (res.data.importadas > 0) fetchCompras();
    } catch (err) {
      setImportResult({ ok: false, error: err.response?.data?.error || err.message });
    }
    setImportLoading(false);
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1.5, gap: 1, bgcolor: t => alpha(t.palette.primary.main, 0.06), borderRadius: 2 }}>
        <ShoppingCart color="primary" sx={{ fontSize: 28 }} />
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={700} color="primary.dark">Registro de Compras</Typography>
          <Typography variant="caption" color="text.secondary">Gestión de compras y control de inventario</Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<GridOnIcon />}
          onClick={() => { setImportDialog(true); setImportFile(null); setImportResult(null); }}
          sx={{ mr: 1 }}>
          Importar Excel
        </Button>
        <Button variant="contained" startIcon={<Add />} onClick={handleNuevo}>
          Nueva Compra
        </Button>
      </Paper>

      {/* Búsqueda */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          size="small" placeholder="Buscar proveedor, N° documento..."
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
          sx={{ flex: '1 1 220px' }}
        />
        <TextField type="date" label="Desde" size="small" sx={{ minWidth: 145 }}
          value={desde} onChange={e => { setDesde(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }} />
        <TextField type="date" label="Hasta" size="small" sx={{ minWidth: 145 }}
          value={hasta} onChange={e => { setHasta(e.target.value); setPage(1); }}
          InputLabelProps={{ shrink: true }} />
        <Button variant="outlined" size="small" onClick={() => { setSearch(''); setDesde(''); setHasta(''); setPage(1); }}>Limpiar</Button>
        <Button variant="contained" size="small" color="success" startIcon={<GridOnIcon />}
          onClick={handleExportExcel} disabled={compras.length === 0}>Excel</Button>
        <IconButton onClick={fetchCompras} disabled={loading} color="primary"><Refresh /></IconButton>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              {['#', 'Fecha', 'Tipo', 'Serie-Número', 'Proveedor', 'Almacén', 'Total', 'Estado', 'Acciones'].map(h => (
                <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>Cargando...</TableCell></TableRow>
            ) : compras.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No hay compras registradas</TableCell></TableRow>
            ) : compras.map((c, i) => (
              <TableRow key={c.id} hover sx={{ bgcolor: i % 2 === 0 ? 'grey.50' : 'white' }}>
                <TableCell sx={{ color: 'text.secondary' }}>{c.id}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{c.fecha_emision?.slice(0, 10)}</TableCell>
                <TableCell>
                  <Chip label={c.tipo_comprobante_proveedor} size="small" variant="outlined" color="primary" />
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{c.serie}-{c.numero}</TableCell>
                <TableCell>{c.proveedor_nombre || '-'}</TableCell>
                <TableCell>{c.almacen_nombre || '-'}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: 'success.dark' }}>
                  {monedaSimbolo(c.moneda)} {parseFloat(c.total || 0).toFixed(2)}
                </TableCell>
                <TableCell>
                  <Chip label={c.estado} size="small"
                    color={c.estado === 'ANULADO' ? 'error' : 'success'}
                    sx={{ fontWeight: 600 }} />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Ver detalle">
                      <IconButton size="small" color="info" onClick={() => handleVerDetalle(c)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Ver PDF">
                      <IconButton size="small" color="secondary" onClick={() => handlePdf(c.id)}>
                        <PictureAsPdf fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={c.adjunto_path ? 'Ver / editar adjunto' : 'Adjuntar archivo'}>
                      <IconButton size="small" color={c.adjunto_path ? 'info' : 'default'}
                        onClick={() => { setAdjuntoDialogCompra(c); setAdjuntoDialogFile(null); setOpenAdjuntoDialog(true); }}>
                        <AttachFile fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {c.estado !== 'ANULADO' && (
                      <Tooltip title="Anular compra">
                        <IconButton size="small" color="error" onClick={() => handleAnular(c.id)}>
                          <Cancel fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">Total: {totalCompras} registros</Typography>
        <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
        <Typography variant="caption">Pág. {page}</Typography>
        <Button size="small" disabled={compras.length < 50} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
      </Box>

      {/* ─── Diálogo nueva compra ──────────────────────────────────────────── */}
      <Dialog open={open} onClose={(_, reason) => { if (reason !== 'backdropClick') setOpen(false); }} maxWidth="xl" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCart />
            <Box>
              <Typography variant="h6" fontWeight={700}>Nueva Compra</Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>Complete los datos del comprobante del proveedor</Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 2.5 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Sección: Proveedor */}
          <SectionHeader>Proveedor y Comprobante</SectionHeader>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={7} sx={{ minWidth: '30em' }}>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={proveedores}
                  getOptionLabel={o => `${o.numero_documento || ''} - ${o.razon_social || ''}`}
                  filterOptions={(options, { inputValue }) => {
                    const q = inputValue.toLowerCase();
                    if (!q) return options;
                    return options.filter(o =>
                      (o.numero_documento || '').includes(q) ||
                      (o.razon_social || '').toLowerCase().includes(q)
                    );
                  }}
                  value={form.proveedor_obj}
                  inputValue={provInputValue}
                  onOpen={() => { if (proveedores.length === 0) fetchProveedoresInit(); }}
                  onInputChange={(_, v) => setProvInputValue(v)}
                  onChange={(_, v) => {
                    setProvInputValue(v ? `${v.numero_documento} - ${v.razon_social}` : '');
                    handleFormChange('proveedor_id', v?.id || '');
                    handleFormChange('proveedor_obj', v);
                  }}
                  renderInput={p => <TextField {...p} label="Proveedor *" size="small" fullWidth />}
                  noOptionsText={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <span>No encontrado</span>
                      <Button size="small" startIcon={<PersonAdd />} onClick={() => setOpenNuevoProv(true)}>
                        Registrar proveedor
                      </Button>
                    </Box>
                  }
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                />
                <Tooltip title="Registrar nuevo proveedor">
                  <IconButton size="small" color="primary" onClick={() => setOpenNuevoProv(true)}>
                    <PersonAdd fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3} sx={{ minWidth: '13em' }}>
              <TextField select label="Tipo Comprobante" size="small" fullWidth
                value={form.tipo_comprobante_proveedor}
                onChange={e => handleFormChange('tipo_comprobante_proveedor', e.target.value)}>
                {TIPOS_COMPROBANTE.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={4} sm={2} sx={{ minWidth: '7em', maxWidth: '7em' }}>
              <TextField label="Serie" size="small" fullWidth
                value={form.serie}
                onChange={e => handleFormChange('serie', e.target.value.toUpperCase())} />
            </Grid>
            <Grid item xs={4} sm={2} sx={{ minWidth: '9em' }}>
              <TextField label="Número *" size="small" fullWidth
                value={form.numero}
                onChange={e => handleFormChange('numero', e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={3} sx={{ minWidth: '11em' }}>
              <TextField label="Fecha Emisión *" type="date" size="small" fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.fecha_emision}
                onChange={e => handleFormChange('fecha_emision', e.target.value)} />
            </Grid>
            <Grid item xs={6} sm={3} sx={{ minWidth: '11em' }}>
              <TextField label="Fecha Vencimiento" type="date" size="small" fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.fecha_vencimiento}
                onChange={e => handleFormChange('fecha_vencimiento', e.target.value)} />
            </Grid>
          </Grid>

          {/* Sección: Logística */}
          <SectionHeader>Logística y Condiciones</SectionHeader>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6} sm={2} sx={{ minWidth: '9em' }}>
              <TextField select label="Moneda" size="small" fullWidth
                value={form.moneda}
                onChange={e => handleFormChange('moneda', e.target.value)}>
                <MenuItem value="PEN">PEN (S/)</MenuItem>
                <MenuItem value="USD">USD ($)</MenuItem>
              </TextField>
            </Grid>
            {form.moneda === 'USD' && (
              <Grid item xs={6} sm={2} sx={{ minWidth: '10em' }}>
                <TextField label="Tipo de Cambio" type="number" size="small" fullWidth
                  value={form.tipo_cambio}
                  onChange={e => handleFormChange('tipo_cambio', e.target.value)} />
              </Grid>
            )}
            <Grid item xs={6} sm={3} sx={{ minWidth: '16em' }}>
              <TextField select label="Condición Pago" size="small" fullWidth
                value={form.condicion_pago}
                onChange={e => handleFormChange('condicion_pago', e.target.value)}>
                {CONDICIONES_PAGO.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ minWidth: '15.6em' }}>
              <TextField select label="Sucursal" size="small" fullWidth
                value={form.sucursal_id}
                onChange={e => handleFormChange('sucursal_id', e.target.value)}>
                <MenuItem value="">Sin sucursal</MenuItem>
                {sucursales.map(s => (
                  <MenuItem key={s.id} value={s.id}>{s.nombre || `Sucursal ${s.id}`}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={5} sx={{ minWidth: '20em' }}>
              <Autocomplete
                options={almacenes}
                getOptionLabel={o => o.nombre || ''}
                value={form.almacen_obj}
                onChange={(_, v) => {
                  handleFormChange('almacen_id', v?.id || '');
                  handleFormChange('almacen_obj', v);
                  if (v?.sucursal_id && !form.sucursal_id) handleFormChange('sucursal_id', v.sucursal_id);
                }}
                renderInput={p => <TextField {...p} label="Almacén de ingreso *" size="small" fullWidth />}
                isOptionEqualToValue={(o, v) => o.id === v?.id}
                noOptionsText="Sin almacenes"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField label="Observaciones" size="small" fullWidth
                value={form.observaciones}
                onChange={e => handleFormChange('observaciones', e.target.value)} />
            </Grid>
          </Grid>

          {/* Sección: Productos */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              borderLeft: '3px solid', borderColor: 'primary.main',
              pl: 1.5, py: 0.25, bgcolor: t => alpha(t.palette.primary.main, 0.06), borderRadius: '0 4px 4px 0', flexGrow: 1
            }}>
              <Typography variant="subtitle2" color="primary.dark" fontWeight={700}>Detalle de Productos</Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch size="small" checked={preciosConIgv}
                  onChange={e => {
                    const v = e.target.checked;
                    setPreciosConIgv(v);
                    setForm(prev => {
                      const detalles = prev.detalles.map(d => calcDetalle(d, v));
                      return { ...prev, detalles, ...calcTotales(detalles) };
                    });
                  }}
                />
              }
              label={<Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>Precios c/ IGV</Typography>}
              sx={{ mx: 1, mb: 0 }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" variant="outlined" color="secondary" startIcon={<ShoppingCart />}
                onClick={() => { cargarOrdenesCompra(); setOpenOCDialog(true); }}
                sx={{ whiteSpace: 'nowrap' }}>
                Importar OC
              </Button>
              <Button size="small" variant="outlined" startIcon={<Add />} onClick={handleAddDetalle} sx={{ whiteSpace: 'nowrap' }}>
                Agregar producto
              </Button>
            </Box>
          </Box>

          <TableContainer component={Paper} elevation={0} variant="outlined"
            sx={{ mb: 2, overflowX: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
            <Table size="small" sx={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: colW.producto }} />
                <col style={{ width: colW.descripcion }} />
                <col style={{ width: colW.cant }} />
                <col style={{ width: colW.umedida }} />
                <col style={{ width: colW.costo }} />
                <col style={{ width: colW.igv }} />
                <col style={{ width: colW.almacen }} />
                <col style={{ width: colW.subtotal }} />
                <col style={{ width: colW.total }} />
                <col style={{ width: colW.acciones }} />
              </colgroup>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  {[
                    { key: 'producto',    label: 'Producto' },
                    { key: 'descripcion', label: 'Descripción' },
                    { key: 'cant',        label: 'Cant.', align: 'center' },
                    { key: 'umedida',     label: 'U.M.', align: 'center' },
                    { key: 'costo',       label: 'Costo Unit.', align: 'right' },
                    { key: 'igv',         label: 'IGV %', align: 'center' },
                    { key: 'almacen',     label: (
                        <Tooltip title="Vacío = usa el almacén de cabecera">
                          <span>Almacén (opc.)</span>
                        </Tooltip>
                      ) },
                    { key: 'subtotal',    label: 'Subtotal', align: 'right' },
                    { key: 'total',       label: 'Total', align: 'right' },
                    { key: 'acciones',    label: '', align: 'center' },
                  ].map(col => (
                    <TableCell key={col.key} align={col.align} sx={{
                      position: 'relative', color: 'white', fontWeight: 'bold',
                      overflow: 'hidden', whiteSpace: 'nowrap', userSelect: 'none',
                    }}>
                      {col.label}
                      {col.key !== 'acciones' && (
                        <ResizeHandleCompra onMouseDown={e => startResize(col.key, e)} />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {form.detalles.map((det, idx) => (
                  <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'action.hover' }, bgcolor: idx % 2 === 0 ? 'grey.50' : 'white' }}>
                    <TableCell sx={{ overflow: 'hidden' }}>
                      <Tooltip title={det.producto_obj ? `${det.producto_obj.codigo ? det.producto_obj.codigo + ' - ' : ''}${det.producto_obj.nombre || ''}` : ''}>
                        <Autocomplete
                          size="small"
                          options={productOptions[idx] || []}
                          filterOptions={x => x}
                          getOptionLabel={o => `${o.codigo ? o.codigo + ' - ' : ''}${o.nombre || ''}`}
                          value={det.producto_obj || null}
                          inputValue={productInputValues[idx] || ''}
                          onOpen={() => { if (!productOptions[idx] || productOptions[idx].length === 0) loadInitialProducts(idx); }}
                          onInputChange={(_, v) => {
                            setProductInputValues(prev => ({ ...prev, [idx]: v }));
                            setProductoInputActivo({ idx, texto: v });
                            if (v.length === 0 || v.length >= 2) fetchProductosForRow(idx, v);
                          }}
                          onChange={(_, v) => handleProductoSelect(idx, v)}
                          renderInput={p => <TextField {...p} placeholder="Buscar producto..." />}
                          isOptionEqualToValue={(o, v) => o.id === v?.id}
                          noOptionsText={
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <span>No encontrado</span>
                              <Button size="small" startIcon={<Add />} onClick={() => { setProductoInputActivo({ idx, texto: productInputValues[idx] || '' }); setOpenNuevoProducto(true); }}>
                                Nuevo producto
                              </Button>
                            </Box>
                          }
                          sx={{ minWidth: 0, width: '100%', '& .MuiInputBase-input': { textOverflow: 'ellipsis' } }}
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ overflow: 'hidden' }}>
                      <Tooltip title={det.descripcion || ''}>
                      <TextField size="small" fullWidth value={det.descripcion}
                        onChange={e => handleDetalleChange(idx, 'descripcion', e.target.value)}
                        onKeyDown={e => { if (e.key === 'F1') { e.preventDefault(); setBuscadorRowIdx(idx); setOpenBuscadorProducto(true); } }}
                        sx={{ '& .MuiInputBase-input': { textOverflow: 'ellipsis' } }}
                        InputProps={{
                          endAdornment: (
                            <Tooltip title="Buscar producto (F1)">
                              <IconButton size="small" edge="end"
                                onClick={() => { setBuscadorRowIdx(idx); setOpenBuscadorProducto(true); }}>
                                <Search fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ),
                        }}
                      />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={det.cantidad}
                        onChange={e => handleDetalleChange(idx, 'cantidad', e.target.value)}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 60 } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" value={det.unidad_medida}
                        onChange={e => handleDetalleChange(idx, 'unidad_medida', e.target.value.toUpperCase())}
                        inputProps={{ style: { textAlign: 'center', width: 50 } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={det.costo_unitario}
                        onChange={e => handleDetalleChange(idx, 'costo_unitario', e.target.value)}
                        inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', width: 90 } }} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" value={det.igv_porcentaje}
                        onChange={e => handleDetalleChange(idx, 'igv_porcentaje', e.target.value)}
                        inputProps={{ min: 0, max: 100, style: { textAlign: 'center', width: 50 } }} />
                    </TableCell>
                    <TableCell>
                      <TextField select size="small" value={det.almacen_id || ''}
                        onChange={e => handleDetalleChange(idx, 'almacen_id', e.target.value)}
                        sx={{ minWidth: 140 }}>
                        <MenuItem value=""><em style={{ color: '#999' }}>— cabecera</em></MenuItem>
                        {almacenes.map(a => (
                          <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {parseFloat(det.subtotal || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="primary.dark">
                        {parseFloat(det.total || 0).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => handleRemoveDetalle(idx)}
                        disabled={form.detalles.length === 1}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totales */}
          <Grid container justifyContent="flex-end">
            <Grid item xs={12} sm={6} md={4}>
              <Paper elevation={2} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'primary.light' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2" color="text.secondary">Subtotal (sin IGV):</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {monedaSimbolo(form.moneda)} {parseFloat(form.subtotal || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">IGV (18%):</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {monedaSimbolo(form.moneda)} {parseFloat(form.total_igv || 0).toFixed(2)}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight="bold">TOTAL:</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    {monedaSimbolo(form.moneda)} {parseFloat(form.total || 0).toFixed(2)}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Adjunto */}
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" component="label" startIcon={<AttachFile />} size="small">
              {adjuntoFile ? adjuntoFile.name : 'Adjuntar factura/boleta escaneada'}
              <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png"
                onChange={e => setAdjuntoFile(e.target.files[0] || null)} />
            </Button>
            {adjuntoFile && (
              <Typography variant="caption" color="success.main" sx={{ ml: 1 }}>
                ✓ {adjuntoFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2, gap: 1, bgcolor: 'grey.50' }}>
          <Button onClick={() => setOpen(false)} startIcon={<Cancel />} color="inherit">Cancelar</Button>
          <Button variant="contained" onClick={handleGuardar}
            disabled={guardando} startIcon={<CloudUpload />} size="large">
            {guardando ? 'Guardando...' : 'Registrar Compra'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Diálogo ver detalle ───────────────────────────────────────────── */}
      <Dialog open={!!viewCompra} onClose={() => setViewCompra(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        {viewCompra && (
          <>
            <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingCart />
                  <Typography fontWeight={700}>
                    Compra #{viewCompra.id} — {viewCompra.tipo_comprobante_proveedor} {viewCompra.serie}-{viewCompra.numero}
                  </Typography>
                </Box>
                <IconButton sx={{ color: 'white' }} onClick={() => handlePdf(viewCompra.id)} size="small">
                  <PictureAsPdf />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 2.5 }}>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Proveedor</Typography>
                  <Typography fontWeight={600}>{viewCompra.proveedor_nombre || '-'}</Typography>
                  <Typography variant="caption">{viewCompra.proveedor_ruc}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Fecha Emisión</Typography>
                  <Typography fontWeight={600}>{viewCompra.fecha_emision?.slice(0, 10)}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Almacén</Typography>
                  <Typography fontWeight={600}>{viewCompra.almacen_nombre || '-'}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Condición Pago</Typography>
                  <Typography fontWeight={600}>{viewCompra.condicion_pago}</Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography variant="caption" color="text.secondary">Moneda</Typography>
                  <Typography fontWeight={600}>{viewCompra.moneda}</Typography>
                </Grid>
                {viewCompra.observaciones && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Observaciones</Typography>
                    <Typography>{viewCompra.observaciones}</Typography>
                  </Grid>
                )}
              </Grid>

              {Array.isArray(viewCompra.detalles) && (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.main' }}>
                        {['Producto', 'Descripción', 'Cant.', 'U.M.', 'Costo Unit.', 'Almacén', 'Subtotal', 'Total'].map(h => (
                          <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {viewCompra.detalles.map((d, i) => (
                        <TableRow key={i} hover sx={{ bgcolor: i % 2 === 0 ? 'grey.50' : 'white' }}>
                          <TableCell>{d.producto_codigo || d.producto_nombre || '-'}</TableCell>
                          <TableCell>{d.descripcion}</TableCell>
                          <TableCell align="right">{parseFloat(d.cantidad || 0).toFixed(2)}</TableCell>
                          <TableCell>{d.unidad_medida}</TableCell>
                          <TableCell align="right">{monedaSimbolo(viewCompra.moneda)} {parseFloat(d.costo_unitario || 0).toFixed(4)}</TableCell>
                          <TableCell>{d.almacen_det_nombre || <Typography variant="caption" color="text.secondary">{viewCompra.almacen_nombre || '—'}</Typography>}</TableCell>
                          <TableCell align="right">{parseFloat(d.subtotal || 0).toFixed(2)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>{parseFloat(d.total || 0).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Grid container justifyContent="flex-end">
                <Grid item xs={12} sm={5}>
                  <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'primary.light' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">Subtotal:</Typography>
                      <Typography variant="body2">{monedaSimbolo(viewCompra.moneda)} {parseFloat(viewCompra.subtotal || 0).toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">IGV:</Typography>
                      <Typography variant="body2">{monedaSimbolo(viewCompra.moneda)} {parseFloat(viewCompra.total_igv || 0).toFixed(2)}</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography fontWeight="bold">TOTAL:</Typography>
                      <Typography fontWeight="bold" color="primary.main" variant="h6">
                        {monedaSimbolo(viewCompra.moneda)} {parseFloat(viewCompra.total || 0).toFixed(2)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ bgcolor: 'grey.50' }}>
              <Button onClick={() => setViewCompra(null)}>Cerrar</Button>
              <Button variant="contained" startIcon={<PictureAsPdf />} onClick={() => handlePdf(viewCompra.id)} color="secondary">
                Ver PDF
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Dialog Importar Compras desde Excel */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', py: 1.5 }}>
          Importar Compras desde Excel
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Button variant="outlined" size="small" color="info" startIcon={<GridOnIcon />}
              onClick={downloadPlantillaCompras} sx={{ mb: 1 }}>
              Descargar Plantilla Excel
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Cada fila = un ítem. Filas con mismo <b>serie + numero + proveedor_ruc</b> se agrupan en una sola compra.
              Los productos deben existir previamente (use su código). Si el proveedor no existe y se indica la razón social, se crea automáticamente.
            </Typography>
          </Box>
          <Box sx={{ border: '2px dashed', borderColor: 'primary.light', borderRadius: 2, p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
            <input type="file" accept=".xlsx,.xls" id="import-compra-file" style={{ display: 'none' }}
              onChange={e => { setImportFile(e.target.files[0]); setImportResult(null); }} />
            <label htmlFor="import-compra-file">
              <Button variant="contained" component="span" startIcon={<CloudUpload />}>
                Seleccionar archivo Excel
              </Button>
            </label>
            {importFile && (
              <Typography variant="body2" sx={{ mt: 1, color: 'success.dark', fontWeight: 600 }}>
                {importFile.name}
              </Typography>
            )}
          </Box>
          {importResult && (
            <Box sx={{ mt: 2, p: 2, bgcolor: importResult.ok ? 'success.light' : 'error.light', borderRadius: 1 }}>
              {importResult.ok ? (
                <>
                  <Typography fontWeight={700} color="success.dark">Importación completada</Typography>
                  <Typography variant="body2">Compras importadas: <b>{importResult.importadas}</b></Typography>
                  {importResult.detalle?.map((d, i) => (
                    <Typography key={i} variant="body2">• {d.serie}-{d.numero} | {d.proveedor} | {d.items} ítem(s)</Typography>
                  ))}
                  {importResult.erroresDetalle?.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="error.dark" fontWeight={600}>Errores:</Typography>
                      {importResult.erroresDetalle.map((e, i) => (
                        <Typography key={i} variant="body2" color="error.dark">• {e}</Typography>
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <Typography color="error.dark">Error: {importResult.error}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)}>Cerrar</Button>
          <Button variant="contained" onClick={handleImportCompras}
            disabled={!importFile || importLoading}
            startIcon={importLoading ? null : <CloudUpload />}>
            {importLoading ? 'Importando...' : 'Importar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Dialog Importar desde OC ───────────────────────────────────────── */}
      <Dialog open={openOCDialog} onClose={() => setOpenOCDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCart />
          Importar desde Orden de Compra
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {ocLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
          ) : ordenesCompra.length === 0 ? (
            <Alert severity="info">No hay órdenes de compra disponibles para importar.</Alert>
          ) : (
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'secondary.main' }}>
                    {['OC-Nro.', 'Fecha', 'Proveedor', 'Estado', 'Total', 'Ítems', ''].map(h => (
                      <TableCell key={h} sx={{ color: 'white', fontWeight: 700 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ordenesCompra.map(oc => (
                    <TableRow key={oc.id} hover>
                      <TableCell sx={{ fontWeight: 600 }}>OC-{String(oc.numero || '').padStart(5, '0')}</TableCell>
                      <TableCell>{oc.fecha?.slice(0, 10)}</TableCell>
                      <TableCell>{oc.proveedor_nombre || '—'}</TableCell>
                      <TableCell><Chip label={oc.estado} size="small" variant="outlined" /></TableCell>
                      <TableCell align="right">{oc.moneda === 'USD' ? '$' : 'S/'} {parseFloat(oc.total || 0).toFixed(2)}</TableCell>
                      <TableCell>{oc.detalles?.length || 0}</TableCell>
                      <TableCell>
                        <Button size="small" variant="contained" color="secondary"
                          onClick={() => importarOrdenCompra(oc)}>
                          Importar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenOCDialog(false)} variant="outlined">Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Adjunto Management Dialog */}
      <Dialog open={openAdjuntoDialog} onClose={() => { setOpenAdjuntoDialog(false); setAdjuntoDialogCompra(null); setAdjuntoDialogFile(null); }} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Typography variant="h6" fontWeight="bold">Adjunto — Compra {adjuntoDialogCompra?.serie}-{adjuntoDialogCompra?.numero}</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {adjuntoDialogCompra?.adjunto_path ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <AttachFile color="info" />
                <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {adjuntoDialogCompra.adjunto_path.split('/').pop()}
                </Typography>
                <Tooltip title="Descargar">
                  <IconButton size="small" color="info" onClick={() => {
                    const tk = localStorage.getItem('token') || '';
                    const a = document.createElement('a');
                    a.href = `/api/compras/${adjuntoDialogCompra.id}/adjunto-download?token=${tk}`;
                    a.download = adjuntoDialogCompra.adjunto_path.split('/').pop();
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                  }}>
                    <CloudUpload fontSize="small" sx={{ transform: 'scaleY(-1)' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Eliminar adjunto">
                  <IconButton size="small" color="error" disabled={adjuntoDialogLoading} onClick={async () => {
                    if (!window.confirm('¿Eliminar el adjunto de esta compra?')) return;
                    setAdjuntoDialogLoading(true);
                    try {
                      await axios.delete(`/api/compras/${adjuntoDialogCompra.id}/adjunto`);
                      await fetchCompras();
                      setAdjuntoDialogCompra(c => ({ ...c, adjunto_path: null }));
                    } catch (e) { alert(e.response?.data?.error || 'Error al eliminar'); }
                    setAdjuntoDialogLoading(false);
                  }}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">Sin adjunto actualmente.</Typography>
            )}
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                {adjuntoDialogCompra?.adjunto_path ? 'Reemplazar adjunto:' : 'Subir adjunto:'}
              </Typography>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />} size="small" fullWidth>
                {adjuntoDialogFile ? adjuntoDialogFile.name : 'Seleccionar archivo'}
                <input type="file" hidden onChange={e => setAdjuntoDialogFile(e.target.files[0] || null)} />
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenAdjuntoDialog(false); setAdjuntoDialogCompra(null); setAdjuntoDialogFile(null); }} color="inherit">Cerrar</Button>
          {adjuntoDialogFile && (
            <Button variant="contained" disabled={adjuntoDialogLoading} startIcon={adjuntoDialogLoading ? <CircularProgress size={16} /> : <CloudUpload />}
              onClick={async () => {
                setAdjuntoDialogLoading(true);
                try {
                  const fd = new FormData();
                  fd.append('archivo', adjuntoDialogFile);
                  await axios.post(`/api/compras/${adjuntoDialogCompra.id}/adjunto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                  await fetchCompras();
                  setOpenAdjuntoDialog(false); setAdjuntoDialogCompra(null); setAdjuntoDialogFile(null);
                } catch (e) { alert(e.response?.data?.error || 'Error al subir'); }
                setAdjuntoDialogLoading(false);
              }}>
              Guardar
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* PDF Format Dialog - Compra */}
      <Dialog open={openCompraPdfDialog} onClose={cerrarDialogoPdfCompra} maxWidth="sm" fullWidth>
        <DialogTitle>Descargar PDF - Compra</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            Selecciona el formato de impresión. El formato elegido puede quedar como predeterminado.
          </Alert>
          {compraPdfResolucionCargando ? (
            <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">Resolviendo plantilla...</Typography>
            </Box>
          ) : compraPdfResolucion && compraPdfResolucion.plantilla_nombre ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2"><strong>Plantilla:</strong> {compraPdfResolucion.plantilla_nombre}</Typography>
              <Typography variant="body2"><strong>Ámbito:</strong> {compraPdfResolucion.scope === 'Global' ? 'Global (todas las sucursales)' : compraPdfResolucion.scope === 'Specific' ? 'Específica para esta sucursal' : compraPdfResolucion.scope}</Typography>
              {compraPdfResolucion.is_inherited && (
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: '#1976d2' }}>
                  Esta sucursal hereda la asignación de la configuración global
                </Typography>
              )}
            </Alert>
          ) : null}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5, mb: 2 }}>
            {[
              { codigo: 'A4', titulo: 'A4', detalle: 'Formato completo' },
              { codigo: 'A5', titulo: 'A5', detalle: 'Formato compacto' },
              { codigo: 'T80', titulo: 'Ticket 80mm', detalle: 'Impresora térmica' }
            ].map((item) => (
              <Paper
                key={item.codigo}
                onClick={() => {
                  setCompraPdfFormatoSeleccionado(item.codigo);
                  actualizarResolucionFormatoCompra(item.codigo);
                }}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '2px solid',
                  borderColor: compraPdfFormatoSeleccionado === item.codigo ? '#1976d2' : '#d0d7de',
                  backgroundColor: compraPdfFormatoSeleccionado === item.codigo ? '#e3f2fd' : '#fff'
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>{item.titulo}</Typography>
                <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>{item.detalle}</Typography>
                {localStorage.getItem('compra_pdf_formato_default') === item.codigo && (
                  <Chip label="Predeterminado" color="primary" size="small" sx={{ mt: 1, display: 'flex', mx: 'auto', width: 'fit-content' }} />
                )}
              </Paper>
            ))}
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={guardarCompraPdfComoPredeterminado}
                onChange={(e) => setGuardarCompraPdfComoPredeterminado(e.target.checked)}
                color="primary"
              />
            }
            label={`Guardar ${compraPdfFormatoSeleccionado} como formato predeterminado`}
          />
          {compraPdfTarget && (
            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
              Documento ID: {compraPdfTarget.id}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={cerrarDialogoPdfCompra}>Cancelar</Button>
          <Button onClick={descargarPdfCompra} variant="contained">
            Descargar {compraPdfFormatoSeleccionado}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog registro rápido de Proveedor */}
      <ClienteRapidoDialog
        open={openNuevoProv}
        onClose={() => setOpenNuevoProv(false)}
        tipo="PROVEEDOR"
        initialQuery={provInputValue}
        onClienteCreado={(prov) => {
          setProveedores(prev => [prov, ...prev]);
          setProvInputValue(`${prov.numero_documento} - ${prov.razon_social}`);
          handleFormChange('proveedor_id', prov.id);
          handleFormChange('proveedor_obj', prov);
          setOpenNuevoProv(false);
        }}
      />

      {/* Dialog registro rápido de Producto */}
      <ProductoRapidoDialog
        open={openNuevoProducto}
        onClose={() => setOpenNuevoProducto(false)}
        initialNombre={productoInputActivo.texto}
        onProductoCreado={(producto) => {
          const idx = productoInputActivo.idx;
          setProductOptions(prev => ({ ...prev, [idx]: [producto, ...(prev[idx] || [])] }));
          handleProductoSelect(idx, producto);
          setProductInputValues(prev => ({ ...prev, [idx]: producto.nombre || '' }));
          setOpenNuevoProducto(false);
        }}
      />
      <ProductoBuscadorDialog
        open={openBuscadorProducto}
        onClose={() => setOpenBuscadorProducto(false)}
        onSelect={handleBuscadorSelectCompra}
      />
    </Box>
  );
};

export default CompraPage;
